import { includes, map, reduce } from 'lodash'
import mustache from '../../services/tiny-mustache'
import { getImageByName } from '../image_provider'

const PTS_PER_INCH = 72,
  LINE_HEIGHT = 1.2,
  MIN_FONT_SIZE = 4,
  SPECIAL_CHARACTERS = /\\|\(|\)/g

const NAMED_COLORS = {
  red:    { r: 220, g: 50,  b: 50  },
  blue:   { r: 50,  g: 100, b: 220 },
  green:  { r: 50,  g: 180, b: 50  },
  yellow: { r: 230, g: 200, b: 0   },
  orange: { r: 230, g: 130, b: 0   },
  pink:   { r: 230, g: 100, b: 180 },
  purple: { r: 150, g: 50,  b: 200 },
  white:  { r: 255, g: 255, b: 255 },
  black:  { r: 0,   g: 0,   b: 0   },
  grey:   { r: 150, g: 150, b: 150 },
  gray:   { r: 150, g: 150, b: 150 },
}

const resolveColor = function(colorStr) {
  if (NAMED_COLORS[colorStr.toLowerCase()]) {
    return NAMED_COLORS[colorStr.toLowerCase()]
  }
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(colorStr)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 }
}

const parseToken = function(tokenName, tokenValue) {
  const flags = {
    bold: tokenName.includes('b'),
    italic: tokenName.includes('i'),
    hasColor: tokenName.includes('c'),
    hasFont: tokenName.includes('f'),
    hasHighlight: tokenName.includes('h'),
    isIcon: tokenName === 'icon'
  }

  if (flags.isIcon) {
    return { ...flags, content: tokenValue.trim() }
  }

  const parts = tokenValue.split(':')
  const expectations = []
  for (const char of tokenName) {
    if (char === 'h') expectations.push('highlight')
    if (char === 'c') expectations.push('color')
    if (char === 'f') expectations.push('font')
  }

  let color = null
  let font = null
  let highlightColor = null

  for (let idx = 0; idx < expectations.length; idx++) {
    if (expectations[idx] === 'highlight') highlightColor = parts[idx]
    if (expectations[idx] === 'color') color = parts[idx]
    if (expectations[idx] === 'font') font = parts[idx]
  }

  const content = parts.slice(expectations.length).join(':')
  return { ...flags, color, font, highlightColor, content }
}

const parseSegments = function(text) {
  const segments = []
  const tokenRegex = /\[(icon|[bicfh]+):([^\]]+)\]/g
  let lastIndex = 0
  let match

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'rich', bold: false, italic: false, hasColor: false, hasFont: false, hasHighlight: false, content: text.slice(lastIndex, match.index) })
    }

    const [fullMatch, tokenName, tokenValue] = match
    const parsed = parseToken(tokenName, tokenValue)
    segments.push({ type: parsed.isIcon ? 'icon' : 'rich', ...parsed })

    lastIndex = match.index + fullMatch.length
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'rich', bold: false, italic: false, hasColor: false, hasFont: false, hasHighlight: false, content: text.slice(lastIndex) })
  }

  return segments
}

const hasTokens = function(text) {
  return /\[(icon|[bicfh]+):/.test(text)
}

const getLineGeometry = function(layerDimensions, lineIndex, totalLines, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight) {
  const progress = totalLines <= 1 ? 0.5 : lineIndex / (totalLines - 1)
  const leftInset = (cornerTopLeft + (cornerBottomLeft - cornerTopLeft) * progress) * layerDimensions.w
  const rightInset = (cornerTopRight + (cornerBottomRight - cornerTopRight) * progress) * layerDimensions.w
  return {
    x: layerDimensions.x + leftInset,
    w: layerDimensions.w - leftInset - rightInset
  }
}

const splitWordIntoChars = function(doc, word, maxWidth, segment) {
  const parts = []
  let chars = ''
  for (const char of word) {
    const testWidth = doc.getTextWidth(chars + char)
    if (testWidth > maxWidth && chars.length > 0) {
      parts.push({ ...segment, content: chars })
      chars = char
    } else {
      chars += char
    }
  }
  if (chars) parts.push({ ...segment, content: chars })
  return parts
}

const sameSegmentStyle = (a, b) =>
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.hasColor === b.hasColor &&
  a.color === b.color &&
  a.hasFont === b.hasFont &&
  a.font === b.font &&
  a.hasHighlight === b.hasHighlight &&
  a.highlightColor === b.highlightColor

const splitSegmentsIntoLines = function(doc, segments, layerDimensions, iconSize, spaceWidth, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight) {
  const avgWidth = layerDimensions.w * (1 - (cornerTopLeft + cornerTopRight + cornerBottomLeft + cornerBottomRight) / 4)

  const lines = []
  let currentLine = []
  let currentLineWidth = 0
  let lineIndex = 0

  const pushLine = () => {
    if (currentLine.length > 0) {
      lines.push({ segments: currentLine, width: currentLineWidth })
      currentLine = []
      currentLineWidth = 0
      lineIndex++
    }
  }

  let spaceBeforeWord = false

  for (const segment of segments) {
    if (segment.type === 'icon') {
      const segWidth = spaceWidth + iconSize + spaceWidth
      if (currentLineWidth + segWidth > avgWidth) pushLine()
      currentLine.push(segment)
      currentLineWidth += segWidth
      spaceBeforeWord = false
    } else {
      const words = segment.content.split(' ')
      for (let i = 0; i < words.length; i++) {
        const word = words[i]
        if (!word) {
          const last = currentLine[currentLine.length - 1]
          if (!(last && last.type === 'icon' && spaceWidth > 0)) spaceBeforeWord = true
          continue
        }
        const wordWidth = doc.getTextWidth(word)
        const spaceW = (spaceBeforeWord && currentLine.length > 0) ? doc.getTextWidth(' ') : 0
        spaceBeforeWord = false

        if (currentLineWidth + spaceW + wordWidth > avgWidth && currentLine.length > 0) {
          pushLine()
          if (wordWidth > avgWidth) {
            const charParts = splitWordIntoChars(doc, word, avgWidth, segment)
            for (const part of charParts) {
              if (doc.getTextWidth(part.content) + currentLineWidth > avgWidth && currentLine.length > 0) pushLine()
              currentLine.push(part)
              currentLineWidth += doc.getTextWidth(part.content)
            }
          } else {
            // Preserve the leading space in content so the reflowed pass can
            // detect that there was a space before this word in the source text,
            // even though it starts a new first-pass line here.
            currentLine.push({ ...segment, content: spaceW > 0 ? ' ' + word : word })
            currentLineWidth = wordWidth
          }
        } else {
          const lastCurrent = currentLine[currentLine.length - 1]
          if (currentLine.length > 0 && lastCurrent.type === 'rich' && sameSegmentStyle(lastCurrent, segment)) {
            currentLine[currentLine.length - 1] = {
              ...lastCurrent,
              content: lastCurrent.content + ' ' + word
            }
            currentLineWidth += spaceW + wordWidth
          } else {
            currentLine.push({ ...segment, content: spaceW > 0 ? ' ' + word : word })
            currentLineWidth += spaceW + wordWidth
          }
        }
      }
    }
  }
  pushLine()

  const totalLines = lines.length
  const allSegments = lines.flatMap(l => l.segments)
  const reflowedLines = []
  let rCurrentLine = []
  let rCurrentLineWidth = 0
  let rLineIndex = 0

  const pushReflowed = () => {
    if (rCurrentLine.length > 0) {
      reflowedLines.push({ segments: rCurrentLine, width: rCurrentLineWidth })
      rCurrentLine = []
      rCurrentLineWidth = 0
      rLineIndex++
    }
  }

  let rSpaceBeforeWord = false

  for (const segment of allSegments) {
    const geo = getLineGeometry(layerDimensions, rLineIndex, totalLines, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight)

    if (segment.type === 'icon') {
      const segWidth = spaceWidth + iconSize + spaceWidth
      if (rCurrentLineWidth + segWidth > geo.w) pushReflowed()
      rCurrentLine.push(segment)
      rCurrentLineWidth += segWidth
      rSpaceBeforeWord = false
    } else {
      const words = segment.content.split(' ')
      for (let i = 0; i < words.length; i++) {
        const word = words[i]
        if (!word) {
          const last = rCurrentLine[rCurrentLine.length - 1]
          if (!(last && last.type === 'icon' && spaceWidth > 0)) rSpaceBeforeWord = true
          continue
        }
        const wordWidth = doc.getTextWidth(word)
        const spaceW = (rSpaceBeforeWord && rCurrentLine.length > 0) ? doc.getTextWidth(' ') : 0
        rSpaceBeforeWord = false
        const geo2 = getLineGeometry(layerDimensions, rLineIndex, totalLines, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight)

        if (rCurrentLineWidth + spaceW + wordWidth > geo2.w && rCurrentLine.length > 0) {
          pushReflowed()
          if (wordWidth > geo2.w) {
            const charParts = splitWordIntoChars(doc, word, geo2.w, segment)
            for (const part of charParts) {
              const geo3 = getLineGeometry(layerDimensions, rLineIndex, totalLines, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight)
              if (doc.getTextWidth(part.content) + rCurrentLineWidth > geo3.w && rCurrentLine.length > 0) pushReflowed()
              rCurrentLine.push(part)
              rCurrentLineWidth += doc.getTextWidth(part.content)
            }
          } else {
            rCurrentLine.push({ ...segment, content: word })
            rCurrentLineWidth = wordWidth
          }
        } else {
          const lastReflowed = rCurrentLine[rCurrentLine.length - 1]
          if (rCurrentLine.length > 0 && lastReflowed.type === 'rich' && sameSegmentStyle(lastReflowed, segment)) {
            rCurrentLine[rCurrentLine.length - 1] = {
              ...lastReflowed,
              content: lastReflowed.content + ' ' + word
            }
            rCurrentLineWidth += spaceW + wordWidth
          } else {
            rCurrentLine.push({ ...segment, content: spaceW > 0 ? ' ' + word : word })
            rCurrentLineWidth += spaceW + wordWidth
          }
        }
      }
    }
  }
  pushReflowed()

  return reflowedLines
}

const applyRotationTransform = function(doc, centerX, centerY, textRotation) {
  const pageHeight = doc.internal.pageSize.height
  const cx = centerX * PTS_PER_INCH
  const cy = (pageHeight - centerY) * PTS_PER_INCH
  const angleRad = (textRotation * Math.PI) / 180
  const cos = Math.cos(angleRad)
  const sin = Math.sin(angleRad)
  const a = cos, b = sin, c = -sin, d = cos
  const e = cx - cx * cos + cy * sin
  const f = cy - cx * sin - cy * cos
  doc.saveGraphicsState()
  doc.setCurrentTransformationMatrix(a, b, c, d, e, f)
}

export default {
  async render(doc, layer, layerDimensions, item, index, total) {
    const
      { r: textRed, g: textGreen, b: textBlue } = layer.textColor,

      defaultTemplateVars = {
        n0: (index).toString(),
        n: (index+1).toString(),
        q: total.toString()
      },

      textContentTemplateVars = reduce(item, (kvObject, itemPair) => {
        kvObject[itemPair.key] = itemPair.value
        return kvObject
      }, defaultTemplateVars),

      contentToRender = layer.textContentTemplate || textContentTemplateVars[layer.name],
      renderedText = contentToRender ? mustache(contentToRender, textContentTemplateVars) : ""

    doc.setTextColor(textRed, textGreen, textBlue)
    doc.setFontSize(layer.textSize)
    doc.setFontStyle("normal")

    const
      defaultFontFamilies = ["helvetica", "courier", "times", "symbol", "zapfdingbats"],
      isBuiltInFont = includes(defaultFontFamilies, layer.textFontName)

    const setBaseFont = () => {
      if (isBuiltInFont) {
        doc.setFont(layer.textFontName)
        doc.setFontStyle(layer.textFontStyle)
      } else {
        const fontFamilyNoWhitespace = layer.textFontName.replace(/ /g, "")
        const familyWithVariant = `${fontFamilyNoWhitespace}-${layer.textFontStyle}`
        doc.setFont(familyWithVariant)
      }
    }

    setBaseFont()

    const textRotation = layer.textRotation || 0
    const centerX = layerDimensions.x + layerDimensions.w / 2
    const centerY = layerDimensions.y + layerDimensions.h / 2

    // Standard rendering path (no tokens)
    if (!hasTokens(renderedText)) {
      const horizontalAlignment = layer.horizontalAlignment || "left"
      let alignmentOffsetX = 0
      if (horizontalAlignment == "center") alignmentOffsetX += layerDimensions.w / 2
      else if (horizontalAlignment == "right") alignmentOffsetX += layerDimensions.w

      let fontSize = doc.internal.getFontSize(),
        finalX = layerDimensions.x + alignmentOffsetX,
        finalY, splitText, cleanText

      const cornerTopLeft = layer.cornerTopLeft || 0
      const cornerTopRight = layer.cornerTopRight || 0
      const cornerBottomLeft = layer.cornerBottomLeft || 0
      const cornerBottomRight = layer.cornerBottomRight || 0
      const hasCorners = cornerTopLeft > 0 || cornerTopRight > 0 || cornerBottomLeft > 0 || cornerBottomRight > 0

      while (fontSize >= MIN_FONT_SIZE) {
        if (hasCorners) {
          // Split word by word accounting for per-line width
          const avgWidth = layerDimensions.w * (1 - (cornerTopLeft + cornerTopRight + cornerBottomLeft + cornerBottomRight) / 4)
          const words = renderedText.split(' ')
          splitText = []
          let currentLine = ''
          let currentWidth = 0

          for (const word of words) {
            if (!word) continue
            const wordWidth = doc.getTextWidth(word)
            const spaceWidth = currentLine ? doc.getTextWidth(' ') : 0

            if (currentLine && currentWidth + spaceWidth + wordWidth > avgWidth) {
              splitText.push(currentLine)
              currentLine = word
              currentWidth = wordWidth
            } else {
              currentLine = currentLine ? currentLine + ' ' + word : word
              currentWidth += spaceWidth + wordWidth
            }
          }
          if (currentLine) splitText.push(currentLine)

          // Second pass with actual line widths
          const totalLines = splitText.length
          const reflowed = []
          let rLine = ''
          let rWidth = 0

          for (const line of splitText) {
            const words = line.split(' ')

            for (const word of words) {
              const wordWidth = doc.getTextWidth(word)
              const spaceWidth = rLine ? doc.getTextWidth(' ') : 0
              const lineIdx = reflowed.length
              const progress = totalLines <= 1 ? 0.5 : lineIdx / (totalLines - 1)
              const leftInset = (cornerTopLeft + (cornerBottomLeft - cornerTopLeft) * progress) * layerDimensions.w
              const rightInset = (cornerTopRight + (cornerBottomRight - cornerTopRight) * progress) * layerDimensions.w
              const lineWidth = layerDimensions.w - leftInset - rightInset
              if (rLine && rWidth + spaceWidth + wordWidth > lineWidth) {
                reflowed.push(rLine)
                rLine = word
                rWidth = wordWidth
              } else {
                rLine = rLine ? rLine + ' ' + word : word
                rWidth += spaceWidth + wordWidth
              }
            }
          }
          if (rLine) reflowed.push(rLine)
          splitText = reflowed

        } else {
          try {
            const options = { throwOnWordSplit: fontSize != MIN_FONT_SIZE }
            splitText = doc.splitTextToSize(renderedText, layerDimensions.w, options)
          } catch(e) {
            if (e.message == "Splitting a word") {
              fontSize -= 1
              doc.setFontSize(fontSize)
              continue
            } else {
              throw e
            }
          }
        }

        const oneLineHeight = fontSize * LINE_HEIGHT / PTS_PER_INCH,
          textHeight = splitText.length * oneLineHeight

        if (textHeight <= layerDimensions.h || fontSize == MIN_FONT_SIZE) {
          const verticalAlignment = layer.verticalAlignment || "top"
          let alignmentOffsetY = 0
          if (verticalAlignment == "middle") alignmentOffsetY = (layerDimensions.h - textHeight) / 2
          else if (verticalAlignment == "bottom") alignmentOffsetY = layerDimensions.h - textHeight
          finalY = layerDimensions.y + oneLineHeight * 5 / 6 + alignmentOffsetY
          break
        } else {
          fontSize -= 1
          doc.setFontSize(fontSize)
        }
      }

      if (hasCorners) {
        // Render each line with its own X offset based on corner insets
        const totalLines = splitText.length
        const oneLineHeight = fontSize * LINE_HEIGHT / PTS_PER_INCH

        if (textRotation !== 0) applyRotationTransform(doc, centerX, centerY, textRotation)

        splitText.forEach((line, lineIdx) => {
          const cleanLine = !isBuiltInFont ? line : line.replace(SPECIAL_CHARACTERS, (sc) => '\\' + sc)
          const progress = totalLines <= 1 ? 0.5 : lineIdx / (totalLines - 1)
          const leftInset = (cornerTopLeft + (cornerBottomLeft - cornerTopLeft) * progress) * layerDimensions.w
          const rightInset = (cornerTopRight + (cornerBottomRight - cornerTopRight) * progress) * layerDimensions.w
          const lineWidth = layerDimensions.w - leftInset - rightInset
          const lineX = layerDimensions.x + leftInset + (horizontalAlignment === 'center' ? lineWidth / 2 : horizontalAlignment === 'right' ? lineWidth : 0)
          const lineY = finalY + lineIdx * oneLineHeight
          doc.text(cleanLine, lineX, lineY, { align: horizontalAlignment })
        })

        if (textRotation !== 0) doc.restoreGraphicsState()
      } else {
        cleanText = !isBuiltInFont ? splitText : map(splitText, (lineOfText) => {
          return lineOfText.replace(SPECIAL_CHARACTERS, (sc) => '\\' + sc)
        })

        if (textRotation !== 0) {
          applyRotationTransform(doc, centerX, centerY, textRotation)
          doc.text(cleanText, finalX, finalY, { align: horizontalAlignment })
          doc.restoreGraphicsState()
        } else {
          doc.text(cleanText, finalX, finalY, { align: horizontalAlignment })
        }
      }
      return
    }

    // Rich text rendering path
    const segments = parseSegments(renderedText)
    const iconScale = layer.iconScale || 0.8
    const horizontalAlignment = layer.horizontalAlignment || "left"
    const cornerTopLeft = layer.cornerTopLeft || 0
    const cornerTopRight = layer.cornerTopRight || 0
    const cornerBottomLeft = layer.cornerBottomLeft || 0
    const cornerBottomRight = layer.cornerBottomRight || 0

    let fontSize = layer.textSize
    let lines = []
    let oneLineHeight, iconSize, spaceWidth

    while (fontSize >= MIN_FONT_SIZE) {
      doc.setFontSize(fontSize)
      setBaseFont()
      oneLineHeight = fontSize * LINE_HEIGHT / PTS_PER_INCH
      iconSize = oneLineHeight * iconScale
      spaceWidth = doc.getTextWidth(' ') * (layer.iconHorizontalGap !== undefined ? layer.iconHorizontalGap : 0)
      lines = splitSegmentsIntoLines(doc, segments, layerDimensions, iconSize, spaceWidth, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight)
      const totalHeight = lines.length * oneLineHeight
      if (totalHeight <= layerDimensions.h) break
      fontSize--
    }

    const verticalAlignment = layer.verticalAlignment || "top"
    const totalHeight = lines.length * oneLineHeight
    let currentY = layerDimensions.y + oneLineHeight * 5 / 6
    if (verticalAlignment == "middle") currentY += (layerDimensions.h - totalHeight) / 2
    else if (verticalAlignment == "bottom") currentY += layerDimensions.h - totalHeight
    if (currentY < layerDimensions.y + oneLineHeight * 5 / 6) {
      currentY = layerDimensions.y + oneLineHeight * 5 / 6
    }

    const maxY = layerDimensions.y + layerDimensions.h

    if (textRotation !== 0) {
      applyRotationTransform(doc, centerX, centerY, textRotation)
    }

    for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
      const line = lines[lineIdx]
      if (currentY - oneLineHeight * 5 / 6 > maxY) break

      const geo = getLineGeometry(layerDimensions, lineIdx, lines.length, cornerTopLeft, cornerTopRight, cornerBottomLeft, cornerBottomRight)

      const hPad = layer.highlightPadding ? doc.getTextWidth(' ') : 0
      let actualLineWidth = 0
      for (const seg of line.segments) {
        if (seg.type === 'icon') {
          actualLineWidth += spaceWidth + iconSize + spaceWidth
        } else {
          actualLineWidth += doc.getTextWidth(seg.content)
          if (seg.hasHighlight && hPad > 0) actualLineWidth += hPad * 2
        }
      }

      let currentX = geo.x
      if (horizontalAlignment === 'center') {
        currentX = geo.x + (geo.w - actualLineWidth) / 2
      } else if (horizontalAlignment === 'right') {
        currentX = geo.x + geo.w - actualLineWidth
      }

      for (const segment of line.segments) {
        setBaseFont()
        doc.setTextColor(textRed, textGreen, textBlue)
        doc.setFontSize(fontSize)

        if (segment.type === 'icon') {
          currentX += spaceWidth
          const iconVerticalOffset = layer.iconVerticalOffset || 0
          const iconY = currentY - iconSize * 0.85 + iconVerticalOffset
          try {
            const img = await getImageByName(segment.content)
            doc.addImage(img, currentX, iconY, iconSize, iconSize)
            currentX += iconSize + spaceWidth
          } catch(e) {
            doc.text(`[${segment.content}]`, currentX, currentY)
            currentX += doc.getTextWidth(`[${segment.content}]`)
          }

        } else if (segment.type === 'rich') {
          const content = segment.content

          if (segment.bold || segment.italic) {
            if (isBuiltInFont) {
              if (segment.bold && segment.italic) doc.setFontStyle('bolditalic')
              else if (segment.bold) doc.setFontStyle('bold')
              else doc.setFontStyle('italic')
            } else {
              // Custom fonts are registered as "<Family>-<variant>" / "normal".
              // setFontStyle() would silently fall back to Times if the variant
              // isn't registered, so check the font list first.
              const fontFamilyNoWhitespace = layer.textFontName.replace(/ /g, "")
              const fontList = doc.getFontList()
              const candidates = (segment.bold && segment.italic)
                ? ['bolditalic', '700italic', '600italic', '800italic', '900italic']
                : segment.bold
                  ? ['bold', '700', '600', '800', '900', '500']
                  : ['italic', '400italic']
              for (const candidate of candidates) {
                if (fontList[`${fontFamilyNoWhitespace}-${candidate}`]) {
                  doc.setFont(`${fontFamilyNoWhitespace}-${candidate}`)
                  break
                }
              }
              // If no variant is loaded, setBaseFont() already set the right font —
              // just leave it as-is rather than falling back to Times.
            }
          }

          // jsPDF silently drops a leading space in doc.text(), so advance
          // currentX by its width and render only the non-space remainder.
          const hasLeadingSpace = content.charAt(0) === ' '
          const renderContent = hasLeadingSpace ? content.slice(1) : content
          const leadingSpaceWidth = hasLeadingSpace ? doc.getTextWidth(' ') : 0
          const textWidth = doc.getTextWidth(renderContent)

          if (hasLeadingSpace) currentX += leadingSpaceWidth

          if (segment.hasHighlight && segment.highlightColor) {
            const { r: hr, g: hg, b: hb } = resolveColor(segment.highlightColor)
            doc.setFillColor(hr, hg, hb)
            const vPad = oneLineHeight * 0.08
            const rectY = currentY - oneLineHeight * 5 / 6 + vPad
            const rectH = oneLineHeight - vPad * 2
            const rectX = currentX
            const rectW = hPad + textWidth + hPad
            if (layer.highlightRounded) {
              const radius = oneLineHeight * 0.25
              doc.roundedRect(rectX, rectY, rectW, rectH, radius, radius, 'F')
            } else {
              doc.rect(rectX, rectY, rectW, rectH, 'F')
            }
          }

          if (hPad > 0 && segment.hasHighlight) currentX += hPad

          if (segment.hasColor && segment.color) {
            const { r, g, b } = resolveColor(segment.color)
            doc.setTextColor(r, g, b)
          }

          if (segment.hasFont && segment.font) {
            try {
              doc.setFont(segment.font)
            } catch(e) {
              setBaseFont()
            }
          }

          doc.text(renderContent, currentX, currentY)
          currentX += textWidth
          if (segment.hasHighlight && hPad > 0) currentX += hPad
        }
      }

      currentY += oneLineHeight
    }

    if (textRotation !== 0) {
      doc.restoreGraphicsState()
    }
  }
}